import { useState, useEffect } from "react";
import { useTonAddress } from "@tonconnect/ui-react";
import { rpcCall } from "../ws/client";
import { toast } from "./ui/Toast";
import "../styles/TradingPause.scss";

interface Props {
    onTradingPauseChange?: (paused: boolean) => void;
}

export default function TradingPause({ onTradingPauseChange }: Props) {
    // Use raw address format (0:...) to match backend user records.
    const walletAddress = useTonAddress(false);
    const [isPaused, setIsPaused] = useState(false);
    const [loading, setLoading] = useState(false);

    // Load initial status
    useEffect(() => {
        if (!walletAddress) return;

        const loadStatus = async () => {
            try {
                const response = await rpcCall<{ wallet: string }, { trading_paused: boolean }>(
                    "user.get_trading_status",
                    { wallet: walletAddress }
                );
                
                if (response.result) {
                    setIsPaused(response.result.trading_paused);
                }
            } catch (err) {
                console.error("Failed to load trading status:", err);
            }
        };

        loadStatus();
    }, [walletAddress]);

    const togglePause = async () => {
        if (!walletAddress) {
            toast.error("Подключите кошелёк");
            return;
        }

        setLoading(true);
        try {
            const newPaused = !isPaused;
            const response = await rpcCall<
                { wallet: string; paused: boolean },
                { success: boolean; trading_paused: boolean }
            >("user.toggle_trading_pause", {
                wallet: walletAddress,
                paused: newPaused,
            });

            if (response.result?.success) {
                setIsPaused(newPaused);
                toast.success(
                    newPaused 
                        ? "🔕 Торги приостановлены. Ваши ордера скрыты." 
                        : "🔔 Торги возобновлены. Ваши ордера видны."
                );
                if (onTradingPauseChange) {
                    onTradingPauseChange(newPaused);
                }
            }
        } catch (err: any) {
            console.error("Failed to toggle trading pause:", err);
            toast.error(err?.message || "Не удалось изменить статус");
        } finally {
            setLoading(false);
        }
    };

    if (!walletAddress) return null;

    return (
        <div className="trading-pause-container">
            <div className="trading-pause-content">
                <span className="trading-pause-icon">
                    {isPaused ? "🔕" : "🔔"}
                </span>
                <span className="trading-pause-label">
                    {isPaused ? "Торги на паузе" : "Торги активны"}
                </span>
                <label className="trading-pause-toggle">
                    <input
                        type="checkbox"
                        checked={isPaused}
                        onChange={togglePause}
                        disabled={loading}
                        aria-label="Toggle trading pause"
                    />
                    <span className="trading-pause-slider"></span>
                </label>
            </div>
        </div>
    );
}
